"use client";

import {
  DatePicker,
  type DatePickerProps,
} from "@mui/x-date-pickers/DatePicker";
import { DateTime } from "luxon";
import type { ReactNode } from "react";
import {
  Controller,
  useFormContext,
  type ControllerProps,
} from "react-hook-form";

import { FormField } from "../FormField";
import {
  SuggestedPickersDay,
  type SuggestedPickersDayProps,
} from "./SuggestedPickersDay";

export type FormDatePickerProps = {
  name: string;
  label?: ReactNode;
  helperText?: ReactNode;
  rules?: ControllerProps["rules"];
  required?: boolean;
  /** Days marked with the "Suggested" ring (e.g. recommended slots). */
  suggestedDays?: DateTime[];
} & Omit<
  DatePickerProps,
  "value" | "onChange" | "label" | "slots" | "slotProps"
>;

export const FormDatePicker = ({
  name,
  label,
  helperText,
  required,
  rules,
  suggestedDays,
  ...rest
}: FormDatePickerProps) => {
  const { control } = useFormContext();
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        const luxonValue =
          typeof field.value === "string" && field.value
            ? DateTime.fromISO(field.value)
            : null;
        const value = luxonValue && luxonValue.isValid ? luxonValue : null;

        return (
          <FormField
            label={label}
            helperText={fieldState.error?.message ?? helperText}
            error={!!fieldState.error}
            required={required}
            htmlFor={name}
          >
            <DatePicker
              {...rest}
              value={value as unknown as DateTime}
              inputRef={field.ref}
              onChange={(next) => {
                const dt = next as DateTime | null;
                if (dt && dt.isValid) {
                  field.onChange(dt.toISO());
                } else {
                  field.onChange(null);
                }
              }}
              slots={{ day: SuggestedPickersDay }}
              slotProps={{
                day: { suggestedDays } as Partial<SuggestedPickersDayProps>,
                textField: {
                  id: name,
                  name: field.name,
                  onBlur: field.onBlur,
                  error: !!fieldState.error,
                  required,
                  fullWidth: true,
                },
              }}
            />
          </FormField>
        );
      }}
    />
  );
};
